/*
 * A message's HTML, already sanitized on the server, shown in a sandboxed
 * iframe: srcdoc, an opaque origin (no allow-same-origin), and a CSP under
 * which the frame can make no network request at all. Images (inline cid:
 * ones, and remote ones once you choose to show them) are fetched by this
 * page through webmail's own signed links and handed to the frame as data:
 * URLs. The only script the CSP allows is the few lines below that report
 * the content's height, pinned by their hash. Mail can't run script, read
 * webmail's cookies or storage, reach the page around it, or call home.
 */
import { useEffect, useRef, useState } from "react";

// Reports the document's height to the parent, and passes on plain key presses so the keyboard
// shortcuts still work after a click inside the message. The only script the CSP allows.
const SIZER =
  "(function(){var l=0;function s(){var h=Math.ceil(document.documentElement.getBoundingClientRect().height);if(h!==l){l=h;parent.postMessage({hxMailHeight:h},'*')}}new ResizeObserver(s).observe(document.documentElement);addEventListener('load',s);s();addEventListener('keydown',function(e){if(e.metaKey||e.ctrlKey||e.altKey)return;parent.postMessage({hxMailKey:e.key},'*')})})()";

let sizerHash: Promise<string> | null = null;
function hashOfSizer(): Promise<string> {
  sizerHash ??= crypto.subtle.digest("SHA-256", new TextEncoder().encode(SIZER)).then((buf) => btoa(String.fromCharCode(...new Uint8Array(buf))));
  return sizerHash;
}

const LINK_RE = /\/api\/webmail\/(?:proxy|inline)\?t=[A-Za-z0-9_.-]+/g;
const RASTER = /^image\/(png|jpeg|gif|webp|avif)$/;
const BLANK = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

function asDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

/** Fetches every image link in the HTML (a few at a time, no cookies) and swaps in data: URLs. */
async function withImagesInline(html: string, signal: AbortSignal): Promise<string> {
  const links = [...new Set(html.match(LINK_RE) ?? [])];
  if (!links.length) return html;
  const found = new Map<string, string>();
  let next = 0;
  const worker = async () => {
    while (next < links.length && !signal.aborted) {
      const link = links[next++]!;
      try {
        const timeout = AbortSignal.timeout(15_000);
        const res = await fetch(link, { credentials: "omit", signal: AbortSignal.any([signal, timeout]) });
        const blob = res.ok ? await res.blob() : null;
        found.set(link, blob && RASTER.test(blob.type) ? await asDataUrl(blob) : BLANK);
      } catch {
        found.set(link, BLANK);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(6, links.length) }, worker));
  return html.replace(LINK_RE, (link) => found.get(link) ?? BLANK);
}

const BASE_CSS = `
html{color-scheme:light}
body{margin:0;padding:16px 20px;background:#fff;color:#1f2328;font:14px/1.55 "Inter Variable",Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;overflow-wrap:break-word;word-break:break-word}
img{max-width:100%;height:auto}
span[data-hx-blocked]{display:inline-block;vertical-align:middle;box-sizing:border-box;background:#f1f2f5;border:1px dashed #c8ccd6;border-radius:4px}
span[data-hx-blocked="tiny"]{display:none}
table{max-width:100%!important}
td,th{word-break:break-word}
pre{white-space:pre-wrap;overflow-x:auto}
a{color:#bd3a1f}
blockquote{margin:8px 0;padding-left:12px;border-left:3px solid #e2e4ea;color:#4b5563}
*{position:static!important;max-width:100%}
`;

export function MailFrame({ html, title }: { html: string; title: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);
  const [doc, setDoc] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const abort = new AbortController();
    void Promise.all([hashOfSizer(), withImagesInline(html, abort.signal)]).then(([hash, body]) => {
      if (!alive) return;
      const csp = [
        "default-src 'none'",
        "img-src data:",
        "style-src 'unsafe-inline'",
        "font-src data:",
        `script-src 'sha256-${hash}'`,
        "base-uri 'none'",
        "form-action 'none'",
      ].join("; ");
      setDoc(
        `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><meta name="referrer" content="no-referrer"><base target="_blank"><style>${BASE_CSS}</style></head><body>${body}<script>${SIZER}</script></body></html>`,
      );
    });
    return () => {
      alive = false;
      abort.abort();
    };
  }, [html]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== ref.current?.contentWindow) return;
      const data = e.data as { hxMailHeight?: unknown; hxMailKey?: unknown } | null;
      const h = data?.hxMailHeight;
      if (typeof h === "number" && Number.isFinite(h)) setHeight(Math.min(Math.max(h, 40), 30_000));
      // A key pressed inside the message: handled as if pressed on the page (one key name, nothing else).
      const key = data?.hxMailKey;
      if (typeof key === "string" && key.length <= 10) window.dispatchEvent(new KeyboardEvent("keydown", { key, bubbles: true }));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  if (doc === null) return <div style={{ height }} />;
  return (
    <iframe
      ref={ref}
      title={title}
      srcDoc={doc}
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
      referrerPolicy="no-referrer"
      className="block w-full border-0 bg-white"
      style={{ height }}
    />
  );
}
